import { Button } from './ui/button';
import { Printer } from 'lucide-react';

interface ThermalReceiptPrinterFriendlyProps {
  invoice: {
    id: string;
    invoiceNumber: string;
    issueDate: string;
    totalAmount: number;
    subtotal?: number;
    taxAmount?: number;
    discountAmount?: number;
    paymentMethod?: string;
    paymentDetails?: {
      cardType?: string;
      cardLast4?: string;
      authCode?: string;
      changeAmount?: number;
      cashReceived?: number;
      momoCode?: string;
      momoName?: string;
    };
    customer?: {
      name?: string;
      phone?: string;
      email?: string;
      loyaltyNumber?: string;
    };
    lines: Array<{
      id?: string;
      description: string;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      taxRate?: number;
      discountAmount?: number;
      category?: string;
    }>;
    company?: {
      name?: string;
      address?: string;
      phone?: string;
      email?: string;
      website?: string;
      taxId?: string;
      registrationNumber?: string;
      logo?: string;
    };
    location?: {
      name?: string;
      address?: string;
      phone?: string;
    };
    cashier?: {
      name?: string;
      id?: string;
    };
    terminal?: {
      id?: string;
      name?: string;
    };
    metadata?: {
      returnPolicy?: string;
      loyaltyMessage?: string;
      promotionalMessage?: string;
      surveyUrl?: string;
      wifiPassword?: string;
      tableNumber?: string;
      guestCount?: string;
      barSubtotal?: number;
      foodSubtotal?: number;
    };
  };
  onPrint?: () => void;
  compact?: boolean;
}

export function ThermalReceiptPrinterFriendly({ 
  invoice, 
  onPrint, 
  compact = false
}: ThermalReceiptPrinterFriendlyProps) {

  // Configurable width - Set to match actual thermal printer width
  // Reduced to 32 characters to prevent line wrapping on 58mm paper
  const RECEIPT_WIDTH = 32;

  const formatPrice = (amount: number | string | undefined | any): string => {
    // Handle null/undefined
    if (amount === null || amount === undefined) {
      return '0.00';
    }
    
    // Handle Prisma Decimal objects from server (they have a toNumber() method)
    if (amount && typeof amount === 'object') {
      // Try toNumber() method first (Prisma Decimal)
      if (typeof amount.toNumber === 'function') {
        amount = amount.toNumber();
      }
      // Try toString() and parse (fallback for Decimal-like objects)
      else if (typeof amount.toString === 'function') {
        amount = parseFloat(amount.toString());
      }
      // Last resort: try to access internal value
      else if ('d' in amount || 's' in amount || 'e' in amount) {
        amount = Number(amount);
      }
    }
    
    // Convert to number and handle all edge cases
    const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || 0));
    
    // Check if it's a valid number
    if (isNaN(numAmount)) {
      return '0.00';
    }
    
    // Format with 2 decimal places and add comma separators
    return numAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    };
  };

  // Create text-based receipt that thermal printers can handle
  const generateTextReceipt = () => {
    const { date, time } = formatDateTime(invoice.issueDate);
    
    // Helper function to center text
    const centerText = (text: string, width: number = RECEIPT_WIDTH) => {
      if (text.length >= width) return text;
      const padding = Math.max(0, Math.floor((width - text.length) / 2));
      return ' '.repeat(padding) + text;
    };
    
    // Helper function to create justified line with better alignment
    const justifyLine = (left: string, right: string, width: number = RECEIPT_WIDTH) => {
      // Ensure we don't exceed width and have at least 1 space
      if (left.length + right.length >= width) {
        return left.substring(0, width - right.length - 1) + ' ' + right;
      }
      const spaces = width - left.length - right.length;
      return left + ' '.repeat(Math.max(1, spaces)) + right;
    };
    
    const lines = [];
    
    // Add extra spacing at top to push content down past any browser headers
    lines.push('');
    lines.push('');
    lines.push('');
    lines.push('');
    
    // Company header - properly centered with correct width (NO LOGO - thermal printers can't print images reliably)
    lines.push(centerText('IKORO HOUSE LTD'));
    lines.push(centerText('KICUKIRO REBERO'));
    lines.push(centerText('KIGALI, KIGALI +250791874212'));
    lines.push('');
    
    // Dashed line matching width
    lines.push('-'.repeat(RECEIPT_WIDTH));
    
    // Transaction info
    lines.push('Server: ' + (invoice.cashier?.name || 'Unknown'));
    lines.push('Station: ' + (invoice.terminal?.name || 'POS-' + (invoice.terminal?.id || '1')));
    lines.push('.'.repeat(RECEIPT_WIDTH));
    // Order # removed - shown at bottom as Ticket #
    lines.push('');
    
    // Items header
    lines.push('='.repeat(RECEIPT_WIDTH));
    lines.push(centerText('ITEMS PURCHASED'));
    lines.push('-'.repeat(RECEIPT_WIDTH));
    
    // Items - with truncation to ensure single line
    invoice.lines.forEach((line) => {
      // Calculate lineTotal if it's 0 or missing (backend bug fix)
      let lineTotal = line.lineTotal;
      // Convert to number and check if it's 0 or falsy
      const numLineTotal = typeof lineTotal === 'number' ? lineTotal : parseFloat(String(lineTotal || 0));
      if (!numLineTotal || numLineTotal === 0) {
        // Calculate from quantity * unitPrice
        const qty = parseFloat(String(line.quantity || 0));
        const price = parseFloat(String(line.unitPrice || 0));
        lineTotal = qty * price;
      }
      
      const priceText = formatPrice(lineTotal);
      const itemPrefix = `${line.quantity} `;
      // Calculate max description length to fit on one line
      const maxDescLength = RECEIPT_WIDTH - itemPrefix.length - priceText.length - 1;
      const description = line.description.length > maxDescLength 
        ? line.description.substring(0, maxDescLength) 
        : line.description;
      const itemText = itemPrefix + description;
      lines.push(justifyLine(itemText, priceText));
    });
    
    lines.push('');
    
    // Total with better alignment
    lines.push('='.repeat(RECEIPT_WIDTH));
    const totalText = 'rwf ' + formatPrice(invoice.totalAmount);
    lines.push(justifyLine('TOTAL:', totalText));
    lines.push('='.repeat(RECEIPT_WIDTH));
    lines.push('');
    
    // Ticket info - using simple >> instead of special character
    lines.push(centerText('>> Ticket #: ' + invoice.invoiceNumber + ' <<'));
    lines.push(centerText(date.replace(/\//g, '/') + ' ' + time + ' PM'));
    lines.push('');
    
    // MOMO info
    lines.push('*'.repeat(RECEIPT_WIDTH));
    lines.push(centerText('MOMO CODE: 700189'));
    lines.push(centerText('MOMO NAME: IKORO HOUSE Ltd'));
    lines.push('  '); // ESC d 5 (feed 5 lines)
    
    // Partial cut command (safer than full cut, works on most printers)
    lines.push('  '); // GS V 1 (partial cut)
    
    // Alternative: Full cut command (uncomment if partial cut doesn't work)
    // lines.push('\x1D\x56\x00'); // GS V 0 (full cut)
    
    // Feed additional lines after cut to ensure paper is ejected
    lines.push('  ');
    lines.push(centerText('THANK YOU!'));

    lines.push('*'.repeat(RECEIPT_WIDTH));
   ;
    lines.push('');
    lines.push('');
    lines.push('');
    
    // CRITICAL: MASSIVE spacing for paper cutting and feeding
    // This forces the printer to physically cut before next receipt
    for (let i = 0; i < 20; i++) {
      lines.push('');
    }
    
    // Add form feed character to force page eject
    lines.push('\f'); // Form feed character
    
    // Add ESC/POS commands for paper cutting (works with most thermal printers)
    // These are raw printer commands that force a paper cut
    // GS V 0 = Full cut (0x1D 0x56 0x00)
    // GS V 1 = Partial cut (0x1D 0x56 0x01)
    // ESC d n = Feed n lines (0x1B 0x64 n)
    
    // Feed 5 lines before cutting to ensure content is past the cutter
    lines.push('  '); // ESC d 5 (feed 5 lines)
    
    // Partial cut command (safer than full cut, works on most printers)
    lines.push('  '); // GS V 1 (partial cut)
    
    // Alternative: Full cut command (uncomment if partial cut doesn't work)
    // lines.push('\x1D\x56\x00'); // GS V 0 (full cut)
    
    // Feed additional lines after cut to ensure paper is ejected
    lines.push('  '); // ESC d 3 (feed 3 more lines)
    
    return lines.join('\n');
  };

  const generateReceiptHTML = () => {
    const textContent = generateTextReceipt();
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title></title>
    <style>
        @page { 
            size: 58mm auto; 
            margin: 0; 
            padding: 0;
            /* Disable all browser headers and footers */
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box;
        }
        body { 
            font-family: 'Courier New', monospace; 
            font-size: 9px; 
            line-height: 1.3; 
            width: 58mm; 
            background: white; 
            color: black;
            padding: 3mm 2mm 8mm 2mm;
            white-space: pre-wrap;
            /* Force page break after content */
            page-break-after: always;
        }
        @media print {
            @page { 
                size: 58mm auto; 
                margin: 0; 
                padding: 0;
                /* Force no headers/footers */
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            body { 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                padding: 3mm 2mm 8mm 2mm;
                /* Critical: Force page break after each receipt */
                page-break-after: always;
            }
            /* Hide any browser-generated content */
            title, 
            head title {
                display: none !important;
            }
        }
    </style>
</head>
<body>${textContent}</body>
</html>`;
  };

  const handlePrint = () => {
    try {
      const printContent = generateReceiptHTML();
      
      // Try different approach to prevent browser headers
      const printWindow = window.open('about:blank', '_blank', 'width=400,height=600,scrollbars=no,resizable=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no');
      if (!printWindow) {
        alert('Please allow popups for printing');
        return;
      }
      
      // Clear any existing content and write new content
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Ensure no title is set and try to clear any browser-generated content
      printWindow.document.title = '';
      
      // Try to remove any meta tags that might cause headers
      const metaTags = printWindow.document.getElementsByTagName('meta');
      for (let i = metaTags.length - 1; i >= 0; i--) {
        metaTags[i].remove();
      }
      
      // Wait for content to load then print
      printWindow.onload = () => {
        // Longer delay to ensure content is fully rendered
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          
          // CRITICAL: Wait longer before closing to ensure print job completes
          // This prevents the next print from starting before this one finishes
          setTimeout(() => {
            printWindow.close();
            onPrint?.();
          }, 3000); // Increased from 1000ms to 3000ms
        }, 500); // Increased from 100ms to 500ms
      };
      
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to print receipt. Please try again.');
    }
  };

  const handleDebug = () => {
    const printContent = generateReceiptHTML();
    const debugWindow = window.open('', '_blank', 'width=400,height=800');
    if (debugWindow) {
      debugWindow.document.write(printContent);
      debugWindow.document.close();
    }
  };

  if (compact) {
    return (
      <div className="flex gap-1">
        <Button
          onClick={handlePrint}
          variant="outline"
          size="sm"
          className="h-7 text-xs"
        >
          <Printer size={12} />
          Receipt
        </Button>
        <Button
          onClick={handleDebug}
          variant="outline"
          size="sm"
          className="h-7 text-xs"
        >
          Debug
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Button
        onClick={handlePrint}
        variant="outline"
        className="flex items-center gap-2"
      >
        <Printer size={16} />
        Receipt
      </Button>
      <Button
        onClick={handleDebug}
        variant="outline"
        className="flex items-center gap-2"
      >
        Debug Layout
      </Button>
    </div>
  );
}